import type { AppProps } from 'next/app'
import { Provider, teamsTheme } from '@fluentui/react-northstar'
import { SessionProvider } from 'next-auth/react'
import { useEffect } from 'react'
import 'primereact/resources/themes/fluent-light/theme.css' //theme
import 'primereact/resources/primereact.min.css' //core css
import 'primeicons/primeicons.css' //icons
import '../styles/globals.css'
import 'styles/customizedPrimeTheme.scss'
import 'styles/darkThemeOverrides.scss'
import Head from 'next/head'
import { useRouter } from 'next/router'
import '@/utils/domPurifyHooks'
import { Tracker } from '@/components/Tracker'
import { Provider as JotaiProvider } from 'jotai'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useTeamsTheme } from '@/hooks/useTeamsTheme'

const PATHS_WITHOUT_SESSION_PROVIDER = ['/teams_logout', '/teams_callback']

const MyApp = ({ Component, pageProps: { ...pageProps } }: AppProps) => {
  const router = useRouter()
  const teamsThemeMode = useTeamsTheme()

  useEffect(() => {
    // Apply dark mode class based on Teams theme or system preference
    if (teamsThemeMode === 'dark') {
      document.documentElement.classList.add('dark')
    } else if (teamsThemeMode === 'light' || teamsThemeMode === 'default') {
      document.documentElement.classList.remove('dark')
    }
    // In case of 'contrast' theme, we keep current setting
  }, [teamsThemeMode])

  const content = (
    <Provider theme={teamsTheme}>
      <JotaiProvider>
        <Head>
          <title key="title">Delayed Send</title>
          <meta key="description" name="description" content="Schedule Microsoft Teams Messages" />
          <meta key="viewport" name="viewport" content="width=device-width, initial-scale=1.0" />
          <link key="icon" rel="icon" href="/logo.png" />
          
          {/* Default OpenGraph tags - will be overridden by page-specific metadata */}
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:url" property="og:url" content="https://delayedsend.com" />
          <meta key="og:title" property="og:title" content="Delayed Send for Microsoft Teams" />
          <meta key="og:description" property="og:description" content="Schedule Microsoft Teams messages to be sent later with advanced timing features" />
          <meta key="og:image" property="og:image" content="https://delayedsend.com/logo.png" />
          
          {/* Twitter */}
          <meta key="twitter:card" property="twitter:card" content="summary_large_image" />
          <meta key="twitter:url" property="twitter:url" content="https://delayedsend.com" />
          <meta key="twitter:title" property="twitter:title" content="Delayed Send for Microsoft Teams" />
          <meta key="twitter:description" property="twitter:description" content="Schedule Microsoft Teams messages to be sent later with advanced timing features" />
          <meta key="twitter:image" property="twitter:image" content="https://delayedsend.com/logo.png" />
        </Head>
        <Component {...pageProps} />
        <ErrorBoundary fallback={<></>}>
          <Tracker />
        </ErrorBoundary>
      </JotaiProvider>
    </Provider>
  )

  // SessionProvider disabled in Teams popups because of session not being deleted while signing out
  return PATHS_WITHOUT_SESSION_PROVIDER.some((path) => router.asPath.includes(path)) ? (
    content
  ) : (
    <SessionProvider>{content}</SessionProvider>
  )
}

export default MyApp
